import { weekStart } from '@fos/shared/contracts/time';
import { addDaysLocal, todayLocal } from '@fos/shared/time';
import { FileText, MessageSquareText, Plus } from 'lucide-react';
import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { errorMessage } from '../../../lib/api';
import { formatClock, formatMoneyAuto } from '../../../lib/format';
import { useScope } from '../../../lib/scope';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { Button } from '../../../ui/Button';
import { Mono } from '../../../ui/Mono';
import { Page } from '../../../ui/Page';
import { PageHeader } from '../../../ui/PageHeader';
import { Segmented } from '../../../ui/Segmented';
import { toast } from '../../../ui/Toast';
import { MobileFab, TOUCH_BUTTON, useIsPhone } from '../../mobile';
import { ModulePage } from '../../modules/pages/ModulePage';
import { OtherWorkspacesHoursSection } from '../../today/components/OtherWorkspaces';
import { AddTimeBar } from '../components/AddTimeBar';
import { AddTimeDialog } from '../components/AddTimeDialog';
import { PartnerSettlements } from '../components/PartnerSettlements';
import { TimeEntryRows } from '../components/TimeEntryRows';
import { TimeReportView } from '../components/TimeReportView';
import { downloadExport } from '../export-download';
import { reportExportUrl, type TimeView, useReportParams, useTimeView } from '../hooks/useReportParams';

/**
 * /czas (Figma 06 Czas: Wpisy 3024:2282, Raport 3025:2413, mobile 3021:2690). „Wpisy / Raport” obok tytułu.
 * Wpisy: pasek dodawania (desktop) albo „Dodaj czas” i „Wpisz zdaniem” (mobile), timesheet tygodniami i dniami,
 * suma tego tygodnia w nagłówku. Raport: okresy, filtry, sumy, tabela; CSV i „Generuj PDF” tylko z „Eksportuje
 * dane”. `?nowy=1` (⌘K „Dodaj czas”) otwiera dialog wpisu. „Moje / Zespół” z menu bocznego.
 * Rozliczenia (DECISIONS 2026-10-08 [P] „Rozliczenie między partnerami”): rozliczenia partnera — dla owner / admin /
 * manager i dla osoby ze stawką partnera; w „Moje” obok sumy tygodnia „Do wypłaty” (nierozliczone kwoty partnera).
 */

export function TimePage() {
  const access = useAccess();
  if (!access.can('time')) return <ModulePage module="time" />;
  return <TimeScreen />;
}

function WeekTotal({ toPay }: { toPay?: { amount: string; currency: string } | null }) {
  const { scope } = useScope();
  const today = todayLocal();
  const from = weekStart(today);
  const week = trpc.time.list.useQuery({ scope, from, to: addDaysLocal(from, 6) });
  if (!week.data) return null;
  return (
    <span className="flex items-baseline gap-6 text-body-s">
      {scope === 'mine' && toPay && (
        <span className="flex items-baseline gap-3" title="Moje do wypłaty: nierozliczone kwoty ze stawką partnera">
          <span className="text-fg-muted">Do wypłaty</span>
          <Mono className="text-body-m text-fg">{formatMoneyAuto(toPay.amount, toPay.currency)}</Mono>
        </span>
      )}
      <span className="flex items-baseline gap-6">
        <span className="text-fg-muted">Ten tydzień</span>
        <Mono className="text-body-m text-fg">{formatClock(week.data.totals.seconds)}</Mono>
      </span>
    </span>
  );
}

function ExportButtons() {
  const { scope } = useScope();
  const [params] = useReportParams();
  const [pending, setPending] = useState<'csv' | 'pdf' | null>(null);
  const run = async (format: 'csv' | 'pdf') => {
    setPending(format);
    try {
      await downloadExport(reportExportUrl(format, params, scope), `czas.${format}`);
    } catch (err) {
      toast.error(errorMessage(err, 'Nie udało się przygotować pliku.'));
    } finally {
      setPending(null);
    }
  };
  return (
    <>
      <Button
        variant="ghost"
        icon={FileText}
        onClick={() => void run('csv')}
        loading={pending === 'csv'}
        className={TOUCH_BUTTON}
      >
        CSV
      </Button>
      <Button
        variant="primary"
        icon={FileText}
        onClick={() => void run('pdf')}
        loading={pending === 'pdf'}
        className={TOUCH_BUTTON}
      >
        Generuj PDF
      </Button>
    </>
  );
}

function TimeScreen() {
  const access = useAccess();
  const [view, setView] = useTimeView();
  const [params, setParams] = useSearchParams();
  const canEdit = access.can('time', 'edit');
  const phone = useIsPhone();
  const [dialog, setDialog] = useState<null | 'form' | 'sentence'>(null);

  // ⌘K „Dodaj czas” → /czas?nowy=1
  const fromUrl = params.get('nowy') === '1' && canEdit;
  const dialogMode = dialog ?? (fromUrl ? 'form' : null);
  const closeDialog = (open: boolean) => {
    if (open) return;
    setDialog(null);
    if (fromUrl) {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('nowy');
          return next;
        },
        { replace: true },
      );
    }
  };

  // rozliczenia partnera: owner / admin / manager albo osoba ze stawką partnera (nierozliczone albo rozliczenia)
  const mine = trpc.time.unbilledSummary.useQuery({ scope: 'mine' });
  const settlements = trpc.time.partnerSettlements.useQuery({}, { enabled: !access.isManagerOrAbove });
  const showSettlements =
    access.isManagerOrAbove || Boolean(mine.data?.partner) || (settlements.data?.items.length ?? 0) > 0;
  const viewOptions: { value: TimeView; label: string }[] = [
    { value: 'wpisy', label: 'Wpisy' },
    { value: 'raport', label: 'Raport' },
    ...(showSettlements ? [{ value: 'rozliczenia' as const, label: 'Rozliczenia' }] : []),
  ];
  const current: TimeView = view === 'rozliczenia' && !showSettlements ? 'wpisy' : view;

  return (
    <Page>
      <PageHeader
        title="Czas"
        aside={
          // telefon: pod tytułem, na pełną szerokość (trzy widoki nie mieszczą się obok „Czas”; Przegląd Fali 11)
          <Segmented
            aria-label="Widok czasu"
            value={current}
            onValueChange={setView}
            options={viewOptions}
            className="max-md:flex max-md:w-full max-md:[&>*]:flex-1 max-md:[&>button]:h-10"
          />
        }
        actions={
          current === 'wpisy' ? (
            <span className="hidden md:inline-flex">
              <WeekTotal toPay={mine.data?.partner ?? null} />
            </span>
          ) : current === 'raport' && access.flag('exportsData') ? (
            <ExportButtons />
          ) : undefined
        }
      />

      {current === 'rozliczenia' ? (
        <div className="mt-6">
          <PartnerSettlements />
        </div>
      ) : current === 'wpisy' ? (
        <div className="mt-6 flex flex-col gap-4 md:gap-6">
          {canEdit && (
            <>
              <AddTimeBar className="hidden md:block" />
              {/* telefon (lifting 17.10.2026): „Dodaj czas” pod kciukiem (MobileFab), „Wpisz zdaniem” nad wpisami */}
              <div className="flex flex-col gap-2 md:hidden">
                <Button
                  variant="ghost"
                  size="l"
                  icon={MessageSquareText}
                  onClick={() => setDialog('sentence')}
                  className="h-11"
                >
                  Wpisz zdaniem
                </Button>
              </div>
              {phone && (
                <MobileFab icon={Plus} onClick={() => setDialog('form')}>
                  Dodaj czas
                </MobileFab>
              )}
            </>
          )}
          {/* telefon: „Do wypłaty” jest na komputerze obok sumy tygodnia — tu jedna linia nad wpisami (Przegląd Fali 11) */}
          {mine.data?.partner && (
            <div
              className="flex items-baseline justify-between gap-3 px-1 text-body-s md:hidden"
              title="Moje do wypłaty: nierozliczone kwoty ze stawką partnera"
            >
              <span className="text-fg-muted">Moje do wypłaty</span>
              <span className="flex items-baseline gap-3">
                <Mono className="text-fg-muted">{formatClock(mine.data.partner.seconds)}</Mono>
                <Mono className="text-body-m text-fg">
                  {formatMoneyAuto(mine.data.partner.amount, mine.data.partner.currency)}
                </Mono>
              </span>
            </div>
          )}
          {/* 8.10: moje godziny w innych workspace'ach (tylko odczyt; brak = nic) */}
          <OtherWorkspacesHoursSection />
          <TimeEntryRows
            framed
            emptyAction={
              canEdit ? (
                <Button variant="secondary" icon={Plus} onClick={() => setDialog('form')} className={TOUCH_BUTTON}>
                  Dodaj czas
                </Button>
              ) : undefined
            }
          />
        </div>
      ) : (
        <div className="mt-6">
          <TimeReportView
            emptyAction={
              <Button variant="secondary" onClick={() => setView('wpisy')} className={TOUCH_BUTTON}>
                Przejdź do wpisów
              </Button>
            }
          />
        </div>
      )}

      {canEdit && (
        <AddTimeDialog
          open={dialogMode !== null}
          onOpenChange={closeDialog}
          initialMode={dialogMode === 'sentence' ? 'sentence' : 'form'}
        />
      )}
    </Page>
  );
}
